<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';

	import { capFirstChar, deepCopy } from '../../../../helpers/utilities';
	import {
		getAvaliableBleGateways,
		isGatewayObserverEnabled,
		setBluetoothRequest,
	} from 'src/helpers/ble_devices_helpers';

	import { rebooting_devices } from 'src/stores/device-runtime/process';
	import { devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';

	//Design system components
	import { Alert, Container, Loader, toast } from '@Shelly/shelly-design-system';

	import SelectList from 'src/components/SelectList/SelectList.svelte';

	import { addToObserverList, removeFromObserverList } from 'src/helpers/observer_lists_helper';
	import { getBluetoothMacFromId, real_id, isWallDisplay } from 'src/helpers/interface_helpers';
	import { cachedLists } from 'src/stores/device-runtime/firmware-ui';
	import type { Device } from 'src/types/Device';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	$: statusSnapshot = $devices_status;
	export let gatewayDisableNext: boolean = false;
	export let associatedDevice: any = null;

	let allItemsRefs: { [key: string | number]: HTMLElement } = {};

	let selected = [];
	let selectedCache = [];
	let loading = false;

	let gateways = deepCopy(getAvaliableBleGateways());

	onMount(async () => {
		loading = true;

		gateways = gateways.map((device) => {
			const hasBLEInList = $cachedLists?.[device.id]?.['ble.cloudrelay.list']?.addrs.includes(
				getBluetoothMacFromId(associatedDevice.id),
			);
			if (hasBLEInList) {
				selected.push(device.id);
				selected = selected;
				selectedCache = JSON.parse(JSON.stringify(selected));
			}

			device.disabled = !statusSnapshot?.[real_id(device.id)]?.online || !device.cloud_online;
			device.descriptiveText =
				!statusSnapshot?.[real_id(device.id)]?.online || !device.cloud_online
					? $format('device_status_no_connection', {
							default: `Device is offline`,
						})
					: undefined;
			device.selected = selected.includes(device.id);

			return device;
		});

		gatewayDisableNext = selected.length <= 0;

		if (import.meta.env.DEV) {
			console.log('%cSELECTED GATEWAYS ON LOAD', 'font-size: 16px; font-weight: bold;');
			if (selected.length > 0) {
				console.log('%cselected', 'color: green;', selected);
				console.log('%cgatewayDisableNext', 'color: green;', gatewayDisableNext);
			} else {
				console.log('%cselected', 'color: red;', selected);
				console.log('%cgatewayDisableNext', 'color: red;', gatewayDisableNext);
			}
		}

		loading = false;
	});

	const rebootDevice = async (device: Device) => {
		if (!isWallDisplay(device.type)) {
			const res = await gen2Request(device, 'Shelly.Reboot', {});
			if (res?.isok) {
				rebooting_devices.update(device.id);
			}
		}
	};

	const handleSelection = (item) => {
		if (selected.includes(item.id)) {
			selected.splice(selected.indexOf(item.id), 1);

			const index = gateways.findIndex((g) => g.id === item.id);
			gateways[index].selected = false;
		} else {
			selected.push(item.id);

			const index = gateways.findIndex((g) => g.id === item.id);
			gateways[index].selected = true;
		}
		selected = selected;

		gatewayDisableNext = selected.length <= 0;

		if (import.meta.env.DEV) {
			console.log('%cSELECTED GATEWAYS ON SELECT/DESELECT', 'font-size: 16px; font-weight: bold;');
			if (selected.length > 0) {
				console.log('%cselected', 'color: green;', selected);
				console.log('%cgatewayDisableNext', 'color: green;', gatewayDisableNext);
			} else {
				console.log('%cselected', 'color: red;', selected);
				console.log('%cgatewayDisableNext', 'color: red;', gatewayDisableNext);
			}
		}
	};

	export const updateGatewaysList = async () => {
		let removed = selectedCache.filter((x) => !selected.includes(x));
		let included = selected.filter((x) => !selectedCache.includes(x));

		// Filter out all selected gateway devices that have no enabled Bluetooth and/or observer. Excludes the devices that have observer always on which will come with firmware version 1.5.0.
		let bleDisabled = selected.filter((x) => !isGatewayObserverEnabled(x));

		let isWallDisplayInAllDevices = false;
		const allDeviceIds = [...removed, ...included, ...bleDisabled];

		await Promise.all(
			included.map(async (id) => {
				const device = $devices[id];

				const resChangeObserverList = await addToObserverList(
					device,
					getBluetoothMacFromId(associatedDevice.id),
				);

				if (resChangeObserverList?.isok) {
					cachedLists.update((list) => {
						if (!list[device.id] || !list[device.id]['ble.cloudrelay.list']) {
							list[device.id] = {};
							list[device.id]['ble.cloudrelay.list'] = {};
							list[device.id]['ble.cloudrelay.list'].addrs = [];
						}
						list[device.id]['ble.cloudrelay.list'].addrs.push(
							getBluetoothMacFromId(associatedDevice.id),
						);
						return list;
					});
				}
			}),
		);

		await Promise.all(
			removed.map(async (id) => {
				const device = $devices[id];
				const resChangeObserverList = await removeFromObserverList(
					device,
					getBluetoothMacFromId(associatedDevice.id),
				);

				if (resChangeObserverList?.isok) {
					cachedLists.update((list) => {
						list[device.id]['ble.cloudrelay.list'].addrs = list[device.id][
							'ble.cloudrelay.list'
						].addrs.filter((addr) => addr !== getBluetoothMacFromId(associatedDevice.id));
						return list;
					});
				}
			}),
		);

		// extracted this on its own and not inside the 'included' devices loop, so that it can work for previously selected devices who's bluetooth is disabled
		await Promise.all(
			bleDisabled.map(async (id) => {
				const device = $devices[id];
				await setBluetoothRequest(device, true, true);
				rebootDevice(device);
			}),
		);

		allDeviceIds.forEach((id) => {
			if (isWallDisplay($devices[id].type)) {
				isWallDisplayInAllDevices = true;
			}
		});

		if (
			allDeviceIds.filter((id, index) => allDeviceIds.indexOf(id) === index).length > 0 &&
			!isWallDisplayInAllDevices
		)
			toast.open({
				type: 'info',
				title: $format('toast_title_gateways_reboot', {
					default: 'The gateways you changed will reboot now.',
				}),
				duration: 5000,
			});

		selectedCache = JSON.parse(JSON.stringify(selected));
	};
</script>

<Container padding={$showNewLayout ? '0' : 'scrollPaddings'}>
	<Alert
		class="mb-s"
		type="info"
		heading={capFirstChar(
			$format('select_ble_gateways_description_v3', {
				default: 'Select Shelly devices to act as Bluetooth gateways for your BLE device.',
			}),
		)}
	/>

	{#if gateways.length == 0}
		<Alert
			class="mb-s"
			type="warning"
			heading={capFirstChar(
				$format('alert_heading_no_gateways', {
					default: 'Missing Bluetooth gateways',
				}),
			)}
			message={$format('alert_message_no_gateways_v2', {
				default:
					"Bluetooth's devices require Shelly Generation 2 and above devices to act as Bluetooth gateways to the cloud.",
			})}
		/>
	{/if}

	{#if loading}
		<div class="d-flex align-items-center justify-content-center">
			<Loader />
		</div>
	{:else}
		<SelectList
			title={$format('label_selected', {
				default: 'Selected:',
			})}
			titleHighlight={selected.length}
			list={gateways}
			bind:allCardsRefs={allItemsRefs}
			on:click={(e) => {
				handleSelection(e.detail);
			}}
		/>

		<!-- DO NOT REMOVE COMMENT: Padding references -->
		<div class="pb-s" />
	{/if}
</Container>
